import type { Router } from "vue-router";

import { TIER } from "@/constants/tier";
import { useAuthenticationStore } from "@/store/authentication.store";
import {
  TITAN_REMOTE_COMPANY_SCOPE_GLOBAL,
  type CompanyScope,
} from "@/remote/contract";

// The current company in the host's vocabulary. Host -> titan only: a return leg made two
// authorities over one company across three routers sharing one `window.history`.

export type ScopeType = CompanyScope["type"];

/** Identity only: what makes two scopes the same company. */
export type OrgRef = { type: ScopeType; uuid: string };

/** Titan's org in the host's vocabulary; the pair above is what identifies it. */
export type CurrentScope = (OrgRef & { name: string }) | null;

export const PARTNER: ScopeType = "partner";
export const CUSTOMER: ScopeType = "customer";

export const isCompanyScope = (value: unknown): value is CompanyScope => {
  const scope = value as CompanyScope | null;
  return (
    typeof scope?.organisationUuid === "string" &&
    scope.organisationUuid !== "" &&
    (scope.type === PARTNER || scope.type === CUSTOMER)
  );
};

const readPublishedScope = (): unknown =>
  (globalThis as Record<string, unknown>)[TITAN_REMOTE_COMPANY_SCOPE_GLOBAL];

/** The scope the host currently believes, or null. */
export const publishedScope = (): CompanyScope | null => {
  const value = readPublishedScope();
  return isCompanyScope(value) ? value : null;
};

/** The type matters as much as the uuid: an MSP viewing a customer keeps its own uuid in
 * `selectedAccount`. */
export const currentScope = (): CurrentScope => {
  const authStore = useAuthenticationStore();

  const customer = authStore.selectedCustomer;
  if (customer?.uuid)
    return { type: CUSTOMER, uuid: customer.uuid, name: customer.name ?? "" };

  const msp = authStore.selectedMsp;
  if (msp?.uuid) return { type: PARTNER, uuid: msp.uuid, name: msp.name ?? "" };

  const account = authStore.selectedAccount;
  if (!account?.uuid) return null;
  const name = account.name ?? "";
  if (account.type_name === TIER.customer)
    return { type: CUSTOMER, uuid: account.uuid, name };
  if (account.type_name === TIER.msps || account.type_name === TIER.distributor)
    return { type: PARTNER, uuid: account.uuid, name };
  return null;
};

/** The signed-in account's own org uuid when System tier, else null. `/system/<uuid>` IS that
 * company's page here; forcing `/msp/<uuid>` 401s into a sign-out loop. */
export const systemAccountOrg = (): string | null => {
  const account = useAuthenticationStore().selectedAccount;
  const uuid = account?.uuid;
  return account?.type_name === TIER.system && typeof uuid === "string" && uuid
    ? uuid
    : null;
};

/** The same company, comparing the host's scope against one of ours. */
export const isSameOrg = (
  current: OrgRef | null,
  published: CompanyScope,
): boolean =>
  current?.type === published.type &&
  current.uuid === published.organisationUuid;

/** `system` is deliberately absent: it is scoped to no company, so it can never satisfy an
 * already-there check. */
export const SCOPE_TYPE_BY_SEGMENT: Record<string, ScopeType> = {
  msp: PARTNER,
  customer: CUSTOMER,
};

/** Read off the router, never `location.pathname`: embedded, that carries the host's base. */
export const routeScope = (router: Router): OrgRef | null => {
  const [segment, uuid] = router.currentRoute.value.path
    .split("/")
    .filter(Boolean);
  if (!segment || !uuid) return null;
  const type = SCOPE_TYPE_BY_SEGMENT[segment];
  return type ? { type, uuid } : null;
};
